import { useEffect } from 'react';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { PricingProfileAccessActionType, PricingProfileType } from 'pricing/enums';
import { pricingProfileService } from 'pricing/services';
import { IPricingProfile } from 'pricing/types';
import { QuotePartAddToPricingProfileOption } from 'quotes/enums';
import { IAddToPricingProfileForm } from 'quotes/types';

interface IProps {
  isOpen?: boolean;
  onClose?: (reset?: UseFormReset<IAddToPricingProfileForm>, closeSelection?: boolean) => void;
  onSubmit?: (
    reset?: UseFormReset<IAddToPricingProfileForm>
  ) => (data: IAddToPricingProfileForm) => void;
  onPricingOptionsChange?: (
    newValue: string,
    oldValue: string,
    setValue: UseFormSetValue<IAddToPricingProfileForm>
  ) => void;
  onAddNewPricingProfile?: (option) => void;
  defaultPricingProfile?: IPricingProfile;
}

const QuotePartAddToPricingProfileFormView = ({
  isOpen,
  onClose,
  onSubmit,
  onPricingOptionsChange,
  onAddNewPricingProfile,
  defaultPricingProfile,
}: IProps) => {
  const { t } = useTranslation();

  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (defaultPricingProfile) {
      setValue('pricingProfile', defaultPricingProfile);
    }
  }, [defaultPricingProfile, setValue]);

  const getRadioGroupChoices = () => {
    return [
      { label: t('labelPrice'), value: QuotePartAddToPricingProfileOption.PRICE },
      { label: t('labelMargin'), value: QuotePartAddToPricingProfileOption.MARGIN },
    ];
  };

  const getPricingProfileAutocompletePayload = () => {
    return {
      filters: {
        access: {
          actionType: PricingProfileAccessActionType.EDIT_PRICING,
        },
        type: [PricingProfileType.DEFAULT, PricingProfileType.PROJECT],
      },
    };
  };

  return (
    <Form
      title={t('titleAddToPricingProfile')}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelAddToPricingProfile')}
    >
      <FormField
        name="pricingProfile"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelPricingProfile'),
          optionKey: 'name',
          required: true,
          getOptions: pricingProfileService.getPricingProfileAutocomplete,
          getLabel: (option) => option?.name || option?.id,
          requestPayload: getPricingProfileAutocompletePayload(),
          hasCreateOption: true,
          onNewOption: onAddNewPricingProfile,
        }}
      />

      <FormField
        name="pricingOptions"
        type={InputType.RADIO_GROUP}
        inputProps={{
          choices: getRadioGroupChoices(),
          defaultValue: QuotePartAddToPricingProfileOption.PRICE,
          onClick: (event) =>
            onPricingOptionsChange(event.target.value, getValues('pricingOptions'), setValue),
        }}
      />

      <FormField
        name="cost"
        type={InputType.CHECKBOX}
        inputProps={{
          label: t('labelCost'),
        }}
      />
    </Form>
  );
};

export default QuotePartAddToPricingProfileFormView;
